import React, { useEffect, useMemo, useState } from 'react';
import { Incarico, IncaricoNote } from "../types";
import { PlusCircle, XCircle, Target, User, MapPin, Calendar, Activity, Clock, CheckCircle2, MessageSquare, RefreshCw, Loader2, X as XIcon, Trash2, PenLine, History } from "lucide-react";
import { ConflittiOrario, estraiConflitti, EsitoConflitti } from "./ConflittiOrario";
import { getStatoOperativoIncarico } from '../utils/incaricoUtils';
import { IncaricoService } from '../services/apiService';

// Badge per stato incarico. ASSEGNATO (ambra) e ACCETTATO (verde acqua) sono
// distinti per vedere subito chi non ha ancora risposto.
const STATO_BADGE: Record<string, { label: string; cls: string; icon?: React.ReactNode }> = {
    ASSEGNATO:  { label: 'In attesa di accettazione', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: <Clock size={11}/> },
    ACCETTATO:  { label: 'Accettato',                  cls: 'bg-teal-50 text-teal-700 border-teal-200',    icon: <CheckCircle2 size={11}/> },
    IN_CORSO:   { label: 'In corso',                   cls: 'bg-green-100 text-green-700 border-green-200', icon: <Activity size={11}/> },
    COMPLETATO: { label: 'Completato',                 cls: 'bg-gray-100 text-gray-600 border-gray-200' },
    RIFIUTATO:  { label: 'Rifiutato',                  cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: <XCircle size={11}/> },
    REVOCATO:   { label: 'Revocato',                   cls: 'bg-red-50 text-red-600 border-red-100', icon: <XCircle size={11}/> },
};

interface Props {
    incarichi: Incarico[];
    onOpenAssign: () => void;
    // Incarico già svolto da inserire con report approvato (es. fermo del gestionale).
    onOpenAPosteriori?: () => void;
    // Revoca: porta l'incarico a REVOCATO senza cancellare sessioni, media e relazioni.
    onRevoca: (id: number) => void;
    // Eliminazione definitiva di un incarico revocato senza sessioni né prove
    // (il BE risponde 409 altrimenti).
    onElimina: (id: number) => void;
    // Dopo il salvataggio delle note il parent ricarica la lista.
    onNoteUpdated?: () => void;
    soggettiList?: { nome: string }[];
}

// Modale "Note operative" (autore, data, testo, GPS). Parte da `noteList`
// dell'incarico; "Aggiorna" rilegge solo le note dal BE.
const NoteOperativeModal: React.FC<{
    incaricoId: number;
    titolo: string;
    initialNote: IncaricoNote[];
    onClose: () => void;
}> = ({ incaricoId, titolo, initialNote, onClose }) => {
    const [note, setNote] = useState<IncaricoNote[]>(initialNote);
    const [loading, setLoading] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const res = await IncaricoService.getNote(incaricoId);
            if (res && (res as any).success) setNote((res as any).data || []);
        } catch {
            // in caso di errore restano le note già caricate
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // All'apertura si rileggono le note, che possono essere cambiate.
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incaricoId]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg shrink-0"><MessageSquare size={16}/></div>
                        <div className="min-w-0">
                            <h3 className="text-sm font-black text-slate-900">Note Operative</h3>
                            <p className="text-[11px] text-slate-500 truncate">{titolo} · {note.length} {note.length === 1 ? 'nota' : 'note'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={refresh} disabled={loading} className="px-2.5 py-1.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                            {loading ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
                            Aggiorna
                        </button>
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-200 transition-colors" title="Chiudi">
                            <XIcon size={18}/>
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50">
                    {note.length === 0 ? (
                        <div className="text-center py-12 text-slate-400 text-sm">
                            <MessageSquare size={32} className="mx-auto mb-3 opacity-30"/>
                            Nessuna nota operativa per questo incarico.
                        </div>
                    ) : (
                        note.map(n => {
                            const autore = n.author
                                ? `${(n.author as any).name || ''} ${(n.author as any).surname || ''}`.trim()
                                : 'Autore N/D';
                            const data = n.createdAt
                                ? new Date(n.createdAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : '';
                            const hasGps = typeof n.lat === 'number' && typeof n.lon === 'number';
                            return (
                                <div key={n.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-black uppercase shrink-0">
                                                {(autore[0] || 'C').toUpperCase()}
                                            </div>
                                            <span className="text-xs font-bold text-slate-800 truncate">{autore}</span>
                                        </div>
                                        <span className="text-[10px] font-bold text-slate-400 shrink-0 flex items-center gap-1">
                                            <Clock size={10}/> {data}
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{n.text}</p>
                                    {hasGps && (
                                        <a
                                            href={`https://www.google.com/maps?q=${n.lat},${n.lon}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold text-blue-600 hover:text-blue-800"
                                        >
                                            <MapPin size={11}/> {n.lat!.toFixed(5)}, {n.lon!.toFixed(5)}
                                        </a>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

// Modifica delle note (direttive) di un incarico via PUT /incarichi/{id}/note.
// Riservata a titolare e responsabile.
const EditNoteModal: React.FC<{
    incarico: Incarico;
    onClose: () => void;
    onSaved: () => void;
}> = ({ incarico, onClose, onSaved }) => {
    const [note, setNote] = useState<string>(incarico.note || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const titolo = `${incarico.collaboratore ? `${incarico.collaboratore.name} ${incarico.collaboratore.surname || ''}`.trim() : 'Collaboratore N/D'}${incarico.soggettoNome ? ` · ${incarico.soggettoNome}` : ''}`;

    const salva = async () => {
        setSaving(true); setError(null);
        try {
            await IncaricoService.updateNote(incarico.id, note.trim());
            onSaved();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Errore durante il salvataggio delle note');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        <PenLine size={18} className="text-blue-600"/>
                        <div>
                            <h3 className="font-bold text-slate-900 text-sm">Note incarico</h3>
                            <p className="text-xs text-slate-500">{titolo}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XIcon size={18}/></button>
                </div>
                <div className="p-5">
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Direttive per il collaboratore</label>
                    <textarea
                        className="w-full p-3 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                        rows={5}
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder="Nessuna nota. Scrivi qui le direttive operative."
                        autoFocus
                    />
                    {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
                </div>
                <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-lg">Annulla</button>
                    <button onClick={salva} disabled={saving} className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 inline-flex items-center gap-2">
                        {saving && <Loader2 size={14} className="animate-spin"/>} Salva note
                    </button>
                </div>
            </div>
        </div>
    );
};

// Avviato = ha già sessioni, foto o report: da lì in poi si modifica solo la fine.
export const incaricoAvviato = (inc: Incarico) =>
    !!inc.activeSessionId
    || (inc.sessioni?.length ?? 0) > 0
    || (inc.media?.length ?? 0) > 0
    || (inc.relazioni?.length ?? 0) > 0;

// Orari dell'incarico. Non avviato: inizio e fine (anche su un altro giorno), con il controllo
// sovrapposizioni della creazione. Avviato: solo la fine; il BE ricalcola `continuativo` e
// risponde 409 se la riduzione lascerebbe fuori sessioni o report.
const EditDataFineModal: React.FC<{
    incarico: Incarico;
    onClose: () => void;
    onSaved: () => void;
}> = ({ incarico, onClose, onSaved }) => {
    const toInput = (iso?: string | null) => (iso ? String(iso).slice(0, 16) : '');
    const soloFine = incaricoAvviato(incarico);
    const [dataInizio, setDataInizio] = useState<string>(toInput(incarico.dataInizio));
    const [dataFine, setDataFine] = useState<string>(toInput(incarico.dataFine));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [conflitti, setConflitti] = useState<EsitoConflitti | null>(null);
    const [confermaSovrapposizione, setConfermaSovrapposizione] = useState(false);
    const titolo = `${incarico.collaboratore ? `${incarico.collaboratore.name} ${incarico.collaboratore.surname || ''}`.trim() : 'Collaboratore N/D'}${incarico.soggettoNome ? ` · ${incarico.soggettoNome}` : ''}`;

    // Vincoli lato UI (li ricontrolla il BE): fine > inizio e non oltre il giorno dopo.
    const inizioRif = soloFine ? toInput(incarico.dataInizio) : dataInizio;
    const minVal = inizioRif || undefined;
    const maxVal = (() => {
        if (!inizioRif) return undefined;
        const d = new Date(inizioRif);
        if (isNaN(d.getTime())) return undefined;
        d.setDate(d.getDate() + 1);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59`;
    })();
    // datetime-local non ha i secondi: li aggiungo per il LocalDateTime del BE.
    const conSecondi = (v: string) => (v.length === 16 ? `${v}:00` : v);

    // Cambiando un orario la sovrapposizione mostrata non vale più.
    const cambia = (setter: (v: string) => void, v: string) => {
        setter(v);
        setConflitti(null);
        setConfermaSovrapposizione(false);
    };

    const salva = async () => {
        if (!dataFine || (!soloFine && !dataInizio)) {
            setError(soloFine ? 'Inserisci una data di fine.' : 'Inserisci inizio e fine.');
            return;
        }
        setSaving(true); setError(null);
        try {
            if (soloFine) {
                await IncaricoService.updateDataFine(incarico.id, conSecondi(dataFine));
            } else {
                await IncaricoService.updateOrari(incarico.id, conSecondi(dataInizio), conSecondi(dataFine),
                    !!conflitti?.overridable && confermaSovrapposizione);
            }
            onSaved();
        } catch (e: any) {
            const c = estraiConflitti(e);
            if (c) {
                setConflitti(c);
                setConfermaSovrapposizione(false);
            } else {
                setError(e?.response?.data?.message || 'Errore durante la modifica degli orari');
            }
            setSaving(false);
        }
    };

    const bloccatoDaConflitti = !!conflitti && (!conflitti.overridable || !confermaSovrapposizione);
    const fmtInizio = incarico.dataInizio
        ? new Date(incarico.dataInizio).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '-';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        <Clock size={18} className="text-indigo-600"/>
                        <div>
                            <h3 className="font-bold text-slate-900 text-sm">{soloFine ? 'Modifica fine incarico' : 'Modifica orari incarico'}</h3>
                            <p className="text-xs text-slate-500">{titolo}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XIcon size={18}/></button>
                </div>
                <div className="p-5 space-y-3">
                    {soloFine ? (
                        <div className="text-xs text-slate-500">
                            Inizio: <span className="font-bold text-slate-700">{fmtInizio}</span>
                            {' · '}l'incarico è già avviato: l'inizio non è più modificabile.
                        </div>
                    ) : (
                        <div>
                            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Nuovo inizio</label>
                            <input
                                type="datetime-local"
                                className="w-full p-3 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-200"
                                value={dataInizio}
                                onChange={e => cambia(setDataInizio, e.target.value)}
                                autoFocus
                            />
                            <p className="text-[11px] text-slate-400 mt-1">Non ancora avviato: puoi spostarlo anche su un altro giorno. Il collaboratore riceve una notifica con il nuovo orario.</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Nuova fine</label>
                        <input
                            type="datetime-local"
                            className="w-full p-3 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-200"
                            value={dataFine}
                            min={minVal}
                            max={maxVal}
                            onChange={e => cambia(setDataFine, e.target.value)}
                            autoFocus={soloFine}
                        />
                        <p className="text-[11px] text-slate-400 mt-1">
                            Può cadere nello stesso giorno dell'inizio o nel giorno successivo (incarico continuativo).
                            {soloFine ? ' Ridurre non è possibile se restano sessioni/foto/report oltre la nuova fine.' : ''}
                        </p>
                    </div>
                    {conflitti && (
                        <ConflittiOrario esito={conflitti} conferma={confermaSovrapposizione} onConferma={setConfermaSovrapposizione}/>
                    )}
                    {error && <p className="text-xs text-red-600">{error}</p>}
                </div>
                <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-lg">Annulla</button>
                    <button onClick={salva} disabled={saving || bloccatoDaConflitti} className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 inline-flex items-center gap-2">
                        {saving && <Loader2 size={14} className="animate-spin"/>} {soloFine ? 'Salva fine' : conflitti ? 'Salva comunque' : 'Salva orari'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const TabIncarichi: React.FC<Props> = ({ incarichi, onOpenAssign, onOpenAPosteriori, onRevoca, onElimina, onNoteUpdated, soggettiList = [] }) => {
    // Ordinamento per dataInizio crescente; quelli senza data in fondo.
    const incarichiOrdinati = useMemo(() => {
        const arr = [...incarichi];
        arr.sort((a, b) => {
            const ta = a.dataInizio ? new Date(a.dataInizio).getTime() : Number.POSITIVE_INFINITY;
            const tb = b.dataInizio ? new Date(b.dataInizio).getTime() : Number.POSITIVE_INFINITY;
            return ta - tb;
        });
        return arr;
    }, [incarichi]);
    // Modale "Note operative".
    const [noteIncarico, setNoteIncarico] = useState<Incarico | null>(null);
    // Incarico di cui si stanno modificando le note.
    const [editNoteIncarico, setEditNoteIncarico] = useState<Incarico | null>(null);
    const [editDataFineIncarico, setEditDataFineIncarico] = useState<Incarico | null>(null);

    return (
    <div className="animate-in fade-in duration-300">

        <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-black text-gray-900">Pianificazione Servizi</h3>
            <div className="flex items-center gap-3">
                {onOpenAPosteriori && (
                    <button onClick={onOpenAPosteriori} className="text-sm text-blue-700 font-bold px-3 py-2.5 rounded-xl hover:bg-blue-50 transition-colors flex items-center gap-2">
                        <History size={18}/> Inserisci incarico già svolto
                    </button>
                )}
                <button onClick={onOpenAssign} className="text-sm bg-blue-600 text-white font-bold px-4 py-2.5 rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm">
                    <PlusCircle size={18}/> Assegna Incarico
                </button>
            </div>
        </div>

        {incarichi.length > 0 ? (
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 text-gray-500 text-[10px] font-bold uppercase tracking-wider border-b border-gray-200">
                    <tr>
                        <th className="p-4"><div className="flex items-center gap-1.5"><Calendar size={14}/> Data Inizio</div></th>
                        <th className="p-4"><div className="flex items-center gap-1.5"><Calendar size={14}/> Data Fine</div></th>
                        <th className="p-4"><div className="flex items-center gap-1.5"><User size={14}/> Collaboratore</div></th>
                        {/* Soggetto */}
                        <th className="p-4"><div className="flex items-center gap-1.5 text-orange-600"><Target size={14}/> Soggetto</div></th>
                        <th className="p-4"><div className="flex items-center gap-1.5"><MapPin size={14}/> Luogo / Note</div></th>
                        <th className="p-4"><div className="flex items-center gap-1.5"><Activity size={14}/> Stato</div></th>
                        {/* Note operative del collaboratore (badge cliccabile) */}
                        <th className="p-4"><div className="flex items-center gap-1.5 text-blue-600"><MessageSquare size={14}/> Note</div></th>
                        <th className="p-4 text-right">Azioni</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                    {incarichiOrdinati.map((inc) => (
                        <tr key={inc.id} className="hover:bg-gray-50 transition-colors group">
                            <td className="p-4 text-sm font-medium text-gray-900">
                                {inc.dataInizio ? new Date(inc.dataInizio).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                {/* #id = stesso identificativo citato nelle email/notifiche ("Incarico #id"). */}
                                <div className="text-[10px] font-mono text-gray-400 mt-0.5">#{inc.id}</div>
                            </td>

                            <td className="p-4 text-sm font-medium text-gray-900">
                                {inc.dataFine ? new Date(inc.dataFine).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>

                            <td className="p-4 text-sm font-bold text-blue-700">
                                {inc.collaboratore ? `${inc.collaboratore.name} ${inc.collaboratore.surname}` : 'Non assegnato'}
                            </td>

                            <td className="p-4">
                                    {(() => {
                                        const nome = (inc as any).soggettoNome || (soggettiList.length === 1 ? soggettiList[0].nome : '');
                                        return nome ? (
                                            <span className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-700 font-bold text-xs px-2.5 py-1 rounded-lg border border-orange-100">
                                                <Target size={12}/>{nome}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-slate-400 italic">—</span>
                                        );
                                    })()}
                            </td>

                            <td className="p-4 text-sm text-gray-600">
                                <div className="font-bold text-gray-800">{inc.luogo}</div>
                                {/* Ultima posizione del collaboratore: città della sessione più recente
                                    (endCityName, o startCityName se ancora aperta). */}
                                {(() => {
                                    const sessioni = ((inc as any).sessioni || []) as any[];
                                    if (sessioni.length === 0) return null;
                                    // Prendi la sessione con il timestamp più recente (end o start).
                                    const ultima = [...sessioni].sort((a, b) => {
                                        const ta = new Date(a.endTime || a.startTime || 0).getTime();
                                        const tb = new Date(b.endTime || b.startTime || 0).getTime();
                                        return tb - ta;
                                    })[0];
                                    const citta = ultima?.endCityName || ultima?.startCityName;
                                    const quando = ultima?.endTime || ultima?.startTime;
                                    if (!citta || !quando) return null;
                                    const minFa = Math.max(0, Math.round((Date.now() - new Date(quando).getTime()) / 60000));
                                    const rel = minFa < 60
                                        ? `${minFa} min fa`
                                        : minFa < 60 * 24
                                            ? `${Math.round(minFa / 60)} h fa`
                                            : `${Math.round(minFa / (60 * 24))} g fa`;
                                    return (
                                        <div className="flex items-center gap-1 text-[10px] text-emerald-700 font-semibold mt-1" title={`Reverse geocoding ${ultima.endTime ? 'fine' : 'inizio'} sessione`}>
                                            <MapPin size={11}/> {citta} · {rel}
                                        </div>
                                    );
                                })()}
                                {inc.note && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{inc.note}</div>}
                            </td>

                            <td className="p-4">
                                {(() => {
                                    // Stesso stato operativo che vede il collaboratore ("In Servizio",
                                    // "Report", "Servizio Espletato"); altrimenti lo stato grezzo.
                                    const op = getStatoOperativoIncarico(inc);
                                    if (op) {
                                        const cls = op.kind === 'espletato' ? 'bg-emerald-600 text-white border-emerald-700'
                                            : op.kind === 'report' ? 'bg-orange-100 text-orange-700 border-orange-200 animate-pulse'
                                            : op.kind === 'pronto' ? 'bg-slate-100 text-slate-600 border-slate-200'
                                            : 'bg-emerald-100 text-emerald-700 border-emerald-200';
                                        return (
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${cls}`}>
                                                {op.kind === 'espletato' && <CheckCircle2 size={11}/>}{op.label}
                                            </span>
                                        );
                                    }
                                    const badge = STATO_BADGE[inc.stato] || { label: inc.stato, cls: 'bg-blue-50 text-blue-600 border-blue-100' };
                                    return (
                                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${badge.cls}`}>
                                            {badge.icon}{badge.label}
                                        </span>
                                    );
                                })()}
                                {inc.inseritoAPosteriori && (
                                    <div className="mt-1">
                                        <span className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600" title="Inserito dal titolare/responsabile dopo lo svolgimento">A posteriori</span>
                                    </div>
                                )}
                                {inc.stato === 'ACCETTATO' && (inc as any).dataAccettazione && (
                                    <div className="text-[9px] text-slate-400 mt-1">
                                        Accettato il {new Date((inc as any).dataAccettazione).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                )}
                                {inc.stato === 'ASSEGNATO' && (inc as any).dataScadenzaAccettazione && (() => {
                                    const ms = new Date((inc as any).dataScadenzaAccettazione).getTime() - Date.now();
                                    const expired = ms <= 0;
                                    return (
                                        <div className={`text-[9px] mt-1 ${expired ? 'text-rose-600 font-bold' : 'text-amber-600'}`}>
                                            {expired ? '⚠ Scadenza superata: ' : 'Scade: '}
                                            {new Date((inc as any).dataScadenzaAccettazione).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    );
                                })()}
                                {inc.stato === 'RIFIUTATO' && (inc as any).motivoRifiuto && (
                                    <div className="text-[9px] text-rose-600 mt-1 italic max-w-[200px]" title={(inc as any).motivoRifiuto}>
                                        {(inc as any).motivoRifiuto}
                                    </div>
                                )}
                            </td>

                            {/* Conteggio note: il click apre la modale con l'elenco */}
                            <td className="p-4">
                                {(() => {
                                    const count = (inc as any).noteList?.length || 0;
                                    if (count === 0) {
                                        return <span className="text-xs text-slate-300 italic">—</span>;
                                    }
                                    return (
                                        <button
                                            onClick={() => setNoteIncarico(inc)}
                                            className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-xs px-2.5 py-1 rounded-lg border border-blue-100 transition-colors"
                                            title="Mostra note operative"
                                        >
                                            <MessageSquare size={12}/>
                                            {count} {count === 1 ? 'nota' : 'note'}
                                        </button>
                                    );
                                })()}
                            </td>

                            <td className="p-4 text-right">
                                {/* Revoca non disponibile per RIFIUTATO, REVOCATO e COMPLETATO */}
                                {/* Modifica note: stessi stati della revoca */}
                                {inc.stato !== 'REVOCATO' && inc.stato !== 'COMPLETATO' && inc.stato !== 'RIFIUTATO' && (
                                    <button
                                        onClick={() => setEditNoteIncarico(inc)}
                                        className="text-slate-500 hover:text-blue-700 px-2 py-1.5 rounded-lg hover:bg-blue-50 transition-colors inline-flex items-center gap-1.5 text-xs font-bold opacity-0 group-hover:opacity-100 mr-1"
                                        title="Modifica note incarico"
                                    >
                                        <PenLine size={14}/> Note
                                    </button>
                                )}
                                {/* Modifica orari (solo la fine se già avviato): stessi stati della revoca */}
                                {inc.stato !== 'REVOCATO' && inc.stato !== 'COMPLETATO' && inc.stato !== 'RIFIUTATO' && (
                                    <button
                                        onClick={() => setEditDataFineIncarico(inc)}
                                        className="text-slate-500 hover:text-indigo-700 px-2 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors inline-flex items-center gap-1.5 text-xs font-bold opacity-0 group-hover:opacity-100 mr-1"
                                        title={incaricoAvviato(inc) ? "Modifica data di fine incarico" : "Modifica orari incarico"}
                                    >
                                        <Clock size={14}/> {incaricoAvviato(inc) ? 'Fine' : 'Orari'}
                                    </button>
                                )}
                                {inc.stato !== 'REVOCATO' && inc.stato !== 'COMPLETATO' && inc.stato !== 'RIFIUTATO' && (
                                    <button
                                        onClick={() => onRevoca(inc.id)}
                                        className="text-red-500 hover:text-red-700 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1.5 ml-auto text-xs font-bold opacity-0 group-hover:opacity-100"
                                        title="Revoca Incarico"
                                    >
                                        <XCircle size={14}/> Revoca
                                    </button>
                                )}
                                {/* Elimina: solo revocati senza sessioni né prove; altrimenti
                                    l'incarico resta come storico. */}
                                {inc.stato === 'REVOCATO'
                                    && (inc.sessioni?.length ?? 0) === 0
                                    && (inc.media?.length ?? 0) === 0 && (
                                    <button
                                        onClick={() => onElimina(inc.id)}
                                        className="text-red-600 hover:text-red-800 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1.5 ml-auto text-xs font-bold opacity-0 group-hover:opacity-100"
                                        title="Elimina definitivamente (nessuna sessione o prova presente)"
                                    >
                                        <Trash2 size={14}/> Elimina
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        ) : (
            <div className="text-center py-12 text-gray-400 border border-dashed border-gray-300 rounded-xl bg-gray-50 flex flex-col items-center gap-3">
                <Calendar size={32} className="text-gray-300"/>
                <div>
                    <p className="font-bold text-gray-500">Nessun incarico pianificato</p>
                    <p className="text-sm">Clicca su "Assegna Incarico" per iniziare la pianificazione.</p>
                </div>
            </div>
        )}

        {/* Modale "Note Operative" */}
        {editNoteIncarico && (
            <EditNoteModal
                incarico={editNoteIncarico}
                onClose={() => setEditNoteIncarico(null)}
                onSaved={() => { setEditNoteIncarico(null); onNoteUpdated?.(); }}
            />
        )}
        {editDataFineIncarico && (
            <EditDataFineModal
                incarico={editDataFineIncarico}
                onClose={() => setEditDataFineIncarico(null)}
                onSaved={() => { setEditDataFineIncarico(null); onNoteUpdated?.(); }}
            />
        )}
        {noteIncarico && (
            <NoteOperativeModal
                incaricoId={noteIncarico.id}
                titolo={`${(noteIncarico.collaboratore ? `${noteIncarico.collaboratore.name} ${noteIncarico.collaboratore.surname}` : 'Collaboratore N/D')}${noteIncarico.soggettoNome ? ` · ${noteIncarico.soggettoNome}` : ''}`}
                initialNote={((noteIncarico as any).noteList || []) as IncaricoNote[]}
                onClose={() => setNoteIncarico(null)}
            />
        )}
    </div>
    );
};